/**
 * Central haptics + sound feedback for Allure.
 *
 * ── Haptics ────────────────────────────────────────────────────────────
 * Uses the Web Vibration API (Android WebView + Android Chrome). No-ops on
 * iOS Safari (Vibration API unavailable) and desktop. Every semantic
 * pattern below is short and non-intrusive.
 *
 * ── Sounds ─────────────────────────────────────────────────────────────
 * Fully synthesized via Web Audio API — zero external assets, sub-KB code,
 * offline-safe. Every sound respects:
 *  1. The user's in-app `sound-enabled` preference (localStorage).
 *  2. The device's ring/silent mode (the browser routes WebAudio through
 *     the media-audio stream, so muted phones stay muted — nothing extra
 *     needed on our side).
 *
 * Sounds default ON. Haptics default ON. Both can be toggled in
 * Profile → Account Settings → Sound & Vibration.
 */

const SOUND_PREF_KEY = 'allure.feedback.sound-enabled';
const HAPTIC_PREF_KEY = 'allure.feedback.haptic-enabled';

// ── preferences ─────────────────────────────────────────────────────────

const readPref = (key) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? true : v === '1';
  } catch (_e) { return true; }
};

const writePref = (key, on) => {
  try { localStorage.setItem(key, on ? '1' : '0'); } catch (_e) { /* ignore */ }
};

export const isSoundEnabled = () => readPref(SOUND_PREF_KEY);
export const setSoundEnabled = (on) => writePref(SOUND_PREF_KEY, on);
export const isHapticEnabled = () => readPref(HAPTIC_PREF_KEY);
export const setHapticEnabled = (on) => writePref(HAPTIC_PREF_KEY, on);

// ── haptic patterns ─────────────────────────────────────────────────────
//
// Two backends:
//   1. `@capacitor/haptics` — used inside the installed Android/iOS app.
//      Ships with the `VIBRATE` permission and native `Vibrator` bindings,
//      so it fires reliably where `navigator.vibrate` silently no-ops.
//   2. `navigator.vibrate` — web/PWA fallback (Chrome on Android has it
//      whitelisted; desktop browsers just no-op).
//
// Haptics plugin is resolved lazily via `window.Capacitor.Plugins.Haptics`
// so we never crash on older bundled APKs that don't include the plugin.

const getHaptics = () => {
  try {
    if (typeof window === 'undefined') return null;
    const cap = window.Capacitor;
    if (!cap || typeof cap.isNativePlatform !== 'function') return null;
    if (!cap.isNativePlatform()) return null;
    return cap.Plugins?.Haptics || null;
  } catch (_e) { return null; }
};

// Map a millisecond duration to a native ImpactStyle.
// Capacitor Haptics exposes `Light` (~10ms), `Medium` (~20ms), `Heavy` (~30ms).
const impactStyleFor = (ms) => {
  if (ms <= 10) return 'LIGHT';
  if (ms <= 20) return 'MEDIUM';
  return 'HEAVY';
};

const vibrateNative = (pattern) => {
  const H = getHaptics();
  if (!H) return false;
  try {
    if (Array.isArray(pattern)) {
      // Sum of ON milliseconds — native vibrate() takes a single duration.
      // Pattern arrays like [15, 40, 25] mean vibrate/pause/vibrate; we
      // approximate by summing the ON pulses so it still "feels" richer
      // than a single tick.
      const total = pattern.filter((_, i) => i % 2 === 0).reduce((a, b) => a + b, 0);
      H.vibrate({ duration: Math.max(10, total) })?.catch?.(() => {});
    } else if (typeof pattern === 'number') {
      // Prefer semantic impact for single-tap patterns — feels sharper
      // than a raw duration vibrate on iOS + newer Androids.
      const style = impactStyleFor(pattern);
      H.impact({ style })?.catch?.(() => {
        H.vibrate({ duration: pattern })?.catch?.(() => {});
      });
    }
    return true;
  } catch (_e) { return false; }
};

const vibrate = (pattern) => {
  if (!isHapticEnabled()) return;
  // 1. Prefer the native Capacitor Haptics plugin on installed apps.
  if (vibrateNative(pattern)) return;
  // 2. Fallback: web Vibration API (works in Chrome/Android browser, PWA).
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  } catch (_e) { /* ignore */ }
};

// Tiny 8 ms bump — nav bar, list rows, small taps.
export const hapticTap = () => vibrate(8);
// Medium 18 ms — primary button tap (e.g., Pay, Scan, Accept).
export const hapticPress = () => vibrate(18);
// Short two-pulse "success" — invite accept, favorite added.
export const hapticSuccess = () => vibrate([15, 40, 25]);
// Warm "arrived" triple-pulse — payment landed, transaction complete.
export const hapticArrive = () => vibrate([20, 40, 30, 40, 20]);
// Wave-hand hello — matches the 5-shake CSS animation cadence.
export const hapticWave = () => vibrate([30, 105, 30, 105, 30, 105, 30, 105, 30]);

// ── sound engine ────────────────────────────────────────────────────────

// Single shared AudioContext — created lazily on first user gesture to
// satisfy browser autoplay policies.
let _ctx = null;
const getCtx = () => {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try { _ctx = new Ctx(); } catch (_e) { return null; }
  return _ctx;
};

/**
 * Play a synthesized tone envelope.
 * @param {Array<{freq:number,type?:string,gain?:number,glideTo?:number}>} voices
 * @param {number} durationMs total note length
 * @param {{attackMs?:number, releaseMs?:number, delayMs?:number}} env
 */
const playTone = (voices, durationMs, env = {}) => {
  if (!isSoundEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;
  // Some mobile browsers park the context until first user gesture.
  if (ctx.state === 'suspended') { try { ctx.resume(); } catch (_e) { /* ignore */ } }

  const start = ctx.currentTime + Math.max(0, (env.delayMs ?? 0) / 1000);
  const dur = Math.max(0.04, durationMs / 1000);
  const attack = Math.max(0.005, (env.attackMs ?? 12) / 1000);
  const release = Math.max(0.01, (env.releaseMs ?? Math.min(220, durationMs - 20)) / 1000);
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, start);
  master.gain.exponentialRampToValueAtTime(1.0, start + attack);
  master.gain.setValueAtTime(1.0, start + Math.max(attack, dur - release));
  master.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  master.connect(ctx.destination);

  voices.forEach(({ freq, type = 'sine', gain = 0.14, glideTo }) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, start);
    if (glideTo != null) osc.frequency.exponentialRampToValueAtTime(glideTo, start + dur);
    g.gain.value = gain;
    osc.connect(g); g.connect(master);
    osc.start(start);
    osc.stop(start + dur + 0.05);
  });
};

/**
 * Play a short filtered-noise burst — used for the "money in flight" whoosh.
 * The bandpass sweeps from filterFromHz → filterToHz over the note's life
 * to feel like something rushing past the ear (Revolut/Apple-Pay style).
 */
const playNoise = ({
  durationMs = 220, filterFromHz = 400, filterToHz = 2200,
  q = 6, gain = 0.14, attackMs = 15, releaseMs = 190,
} = {}) => {
  if (!isSoundEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') { try { ctx.resume(); } catch (_e) { /* ignore */ } }

  const start = ctx.currentTime;
  const dur = Math.max(0.05, durationMs / 1000);

  // Fill a buffer with white noise once per call — cheap enough at these sizes.
  const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = (Math.random() * 2 - 1);

  const src = ctx.createBufferSource();
  src.buffer = buf;

  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = q;
  filter.frequency.setValueAtTime(filterFromHz, start);
  filter.frequency.exponentialRampToValueAtTime(filterToHz, start + dur);

  const g = ctx.createGain();
  const attack = Math.max(0.005, attackMs / 1000);
  const release = Math.max(0.01, releaseMs / 1000);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + attack);
  g.gain.setValueAtTime(gain, start + Math.max(attack, dur - release));
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);

  src.connect(filter); filter.connect(g); g.connect(ctx.destination);
  src.start(start);
  src.stop(start + dur + 0.05);
};

/**
 * FM-synthesized bell tone — a carrier sine whose pitch is modulated by a
 * second oscillator. Produces rich, inharmonic bell/marimba timbres that
 * pure sines can't. Used for the fintech "sent" confirmation blip.
 */
const playFMBell = ({
  carrierHz = 880, modulatorRatio = 1.4, modulatorDepth = 320,
  durationMs = 220, attackMs = 3, releaseMs = 200, gain = 0.11, delayMs = 0,
} = {}) => {
  if (!isSoundEnabled()) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') { try { ctx.resume(); } catch (_e) { /* ignore */ } }

  const start = ctx.currentTime + Math.max(0, delayMs / 1000);
  const dur = Math.max(0.05, durationMs / 1000);
  const attack = Math.max(0.002, attackMs / 1000);
  const release = Math.max(0.01, releaseMs / 1000);

  const carrier = ctx.createOscillator();
  const modulator = ctx.createOscillator();
  const modGain = ctx.createGain();
  const master = ctx.createGain();

  carrier.type = 'sine';
  modulator.type = 'sine';
  carrier.frequency.setValueAtTime(carrierHz, start);
  modulator.frequency.setValueAtTime(carrierHz * modulatorRatio, start);
  // Modulator depth also decays — makes the "clang" fade to a pure sine tail.
  modGain.gain.setValueAtTime(modulatorDepth, start);
  modGain.gain.exponentialRampToValueAtTime(1, start + dur);

  master.gain.setValueAtTime(0.0001, start);
  master.gain.exponentialRampToValueAtTime(gain, start + attack);
  master.gain.setValueAtTime(gain, start + Math.max(attack, dur - release));
  master.gain.exponentialRampToValueAtTime(0.0001, start + dur);

  modulator.connect(modGain);
  modGain.connect(carrier.frequency);   // FM: modulator drives carrier pitch
  carrier.connect(master);
  master.connect(ctx.destination);

  modulator.start(start); carrier.start(start);
  modulator.stop(start + dur + 0.05); carrier.stop(start + dur + 0.05);
};

// ── audio file playback (for custom mp3 assets) ─────────────────────────
//
// Some sounds — currently Guest Paying and Venue Receiving — use custom
// mp3 assets curated by the product owner rather than synth. We cache one
// HTMLAudioElement per URL and clone it on each play so overlapping plays
// don't cut each other off.

const _audioCache = new Map();

const playAudioFile = (url, { volume = 1.0 } = {}) => {
  if (!isSoundEnabled()) return;
  if (typeof window === 'undefined') return;
  try {
    let base = _audioCache.get(url);
    if (!base) {
      base = new Audio(url);
      base.preload = 'auto';
      _audioCache.set(url, base);
    }
    // Clone so rapid taps don't clip each other and we don't fight play/pause.
    const node = base.cloneNode(true);
    node.volume = Math.max(0, Math.min(1, volume));
    const p = node.play();
    if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay blocked pre-gesture */ });
  } catch (_e) { /* ignore */ }
};

// ── semantic sounds ─────────────────────────────────────────────────────

/**
 * Guest "sending" — plays the curated Paying.mp3 asset.
 * Custom sound chosen by product for the 2026 fintech feel.
 */
export const soundSend = () => {
  playAudioFile('/sounds/guest-paying.mp3', { volume: 1.0 });
};

/**
 * Venue "arriving" — plays the curated Payout.mp3 asset.
 * Matched pair with soundSend so send→arrive feels intentional.
 */
export const soundReceive = () => {
  playAudioFile('/sounds/venue-receiving.mp3', { volume: 1.0 });
};

/**
 * Heart-like tick — soft, warm click for the favorite/save action.
 * Reassigned from the old cold "button tap" so it feels affectionate.
 */
export const soundLike = () => playTone(
  [
    { freq: 720, type: 'sine',     gain: 0.10 },
    { freq: 360, type: 'triangle', gain: 0.04 },
  ],
  60,
  { attackMs: 4, releaseMs: 54 },
);

/**
 * Event-bell ping — reuses the same warm tick as heart/favorite so both
 * "small commit" gestures sound and feel identical.
 */
export const soundBell = () => soundLike();

/**
 * Invite-accepted celebration — plays the curated notification asset.
 * Chosen by product to sync with the green checkmark reveal on the
 * invite waiting page, right before the QR pass appears.
 */
export const soundSuccess = () => {
  playAudioFile('/sounds/invite-accepted.mp3', { volume: 1.0 });
};

// ── convenience: paired feedback shortcuts ──────────────────────────────

// Button tap → HAPTIC ONLY. No sound (was too "clicky").
export const feedbackTap        = () => { hapticTap(); };
export const feedbackButton     = () => { hapticPress(); };
// Heart / favorite — soft warm tick + tap haptic.
export const feedbackLike       = () => { hapticTap(); soundLike(); };
// Event bell "remind me" — bright ping + tap haptic.
export const feedbackEventBell  = () => { hapticTap(); soundBell(); };
// Invite accepted — luxury harp + success haptic.
export const feedbackInviteOk   = () => { hapticSuccess(); soundSuccess(); };
// Guest pays — custom Paying.mp3 + press haptic.
export const feedbackPaySend    = () => { hapticPress(); soundSend(); };
// Venue receives / guest sees success — bell chord + arrive haptic.
export const feedbackPayArrived = () => { hapticArrive(); soundReceive(); };
